/* tslint:disable:variable-name */
import { CurrencyPipe } from '@angular/common';
import {
  Directive,
  ElementRef,
  forwardRef,
  HostBinding,
  Inject,
  Input,
  OnDestroy,
  Optional,
  Renderer2,
  Self,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { MatFormField, MatFormFieldControl, MAT_FORM_FIELD } from '@angular/material/form-field';
import { fromEvent, Subject } from 'rxjs';
import { pluck, startWith, tap } from 'rxjs/operators';

const onlyNumbers = /\D/g;

@Directive({
  providers: [
    {
      multi: true,
      provide: MatFormFieldControl,
      useExisting: forwardRef(() => CurrencyMatInputInputDirective),
    },
    CurrencyPipe,
  ],
  selector: '[lohiMatInputCurrencyInput]',
  // tslint:disable-next-line:no-host-metadata-property
  host: {
    '[class.example-floating]': 'shouldLabelFloat',
    '[id]': 'id',
    '[class.outline-none]': 'true',
    '[class.mat-input-element]': 'true',
    '[disabled]': 'disabled',
  },
})
export class CurrencyMatInputInputDirective
  implements ControlValueAccessor, MatFormFieldControl<HTMLInputElement>, OnDestroy {
  public static nextId = 0;
  public touched = false;
  public focused = false;
  public readonly controlType = 'currency-input';
  public readonly autofilled = false;
  public readonly userAriaDescribedBy = 'Currency Input';
  private _placeholder: string;
  private _value: string;
  private _required = false;
  private _disabled = false;
  @HostBinding() public id = `currency-input-${CurrencyMatInputInputDirective.nextId++}`;
  // tslint:disable-next-line
  public stateChanges = new Subject<void>();
  private readonly type: string;
  public onChange: (arg0: any) => void;
  public onTouched: () => void;

  get placeholder() {
    return this._placeholder;
  }

  @Input()
  set placeholder(plh: string) {
    this._placeholder = plh;
    this.stateChanges.next();
  }

  get empty() {
    return !this._value || this._value.length === 0;
  }

  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  public set value(v: any) {
    this.elementRef.nativeElement.setValue(v);
    this.stateChanges.next();
  }

  get required() {
    return this._required;
  }
  @Input()
  set required(req) {
    this._required = !!req;
    this.stateChanges.next();
  }

  get disabled(): boolean {
    if (this.ngControl && this.ngControl.disabled !== null) {
      return this.ngControl.disabled;
    }
    return this._disabled;
  }
  @Input()
  set disabled(value: boolean) {
    this._disabled = !!value;
    this.elementRef.nativeElement.disabled = true;
    this.stateChanges.next();
  }

  constructor(
    private renderer: Renderer2,
    private elementRef: ElementRef,
    @Optional() @Inject(MAT_FORM_FIELD) public _formField: MatFormField,
    @Optional() @Self() public ngControl: NgControl,
    private currencyPipe: CurrencyPipe,
  ) {
    if (this.ngControl != null) {
      // Setting the value accessor directly (instead of using
      // the providers) to avoid running into a circular import.
      this.ngControl.valueAccessor = this;
    }
    this.type = (elementRef.nativeElement as HTMLInputElement).type;
    if (this.type !== 'text') {
      console.warn('currency pipe without text input');
    }
    this.renderer.listen(this.elementRef.nativeElement, 'focus', (e) => {
      this.onFocusIn(e);
    });
    this.renderer.listen(this.elementRef.nativeElement, 'blur', (e) => {
      this.onFocusOut(e);
    });
    fromEvent(this.elementRef.nativeElement, 'input')
      .pipe(
        tap((event: Event) => {
          event.preventDefault();
          event.stopImmediatePropagation();
          event.stopPropagation();
        }),
        pluck('target', 'value'),
        startWith(''),
      )
      .subscribe((value) => {
        const inputOnlyNumberString = (value as string).replace(onlyNumbers, '');
        const inputOnlyNumber =
          parseInt(inputOnlyNumberString, 10) === 0 ? 0 : parseInt(inputOnlyNumberString, 10) || null;
        this.writeValue(inputOnlyNumber);
      });
  }

  public ngOnDestroy() {
    this.stateChanges.complete();
  }

  public writeValue(input: number): void {
    if (input === null) {
      this._value = null;
      this.renderer.setProperty(this.elementRef.nativeElement, 'value', null);
      if (this.onChange) {
        this.onChange(input);
      }
      return;
    }
    const inDollars = input / 100;
    if (this.type === 'text') {
      const formattedField = this.currencyPipe.transform(inDollars);
      this.renderer.setProperty(this.elementRef.nativeElement, 'value', formattedField);
      this._value = formattedField;
    } else {
      this.renderer.setProperty(this.elementRef.nativeElement, 'value', inDollars);
      this._value = `${input}`;
    }
    if (this.onChange) {
      this.onChange(input);
    }
    this.stateChanges.next();
  }

  public registerOnChange(fn: (arg0: any) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.renderer.setProperty(this.elementRef.nativeElement, 'disabled', isDisabled);
  }

  public onFocusIn(event: FocusEvent) {
    if (!this.focused) {
      this.focused = true;
      this.elementRef.nativeElement.focus();
      this.stateChanges.next();
    }
  }

  public onFocusOut(event: FocusEvent) {
    if (!this.elementRef.nativeElement.contains(event.relatedTarget as Element)) {
      this.touched = true;
      this.focused = false;
      this.onTouched();
      this.stateChanges.next();
    }
  }

  public get errorState(): boolean {
    if (this.ngControl) {
      return this.ngControl.invalid && this.touched;
    }
    return true;
  }

  public onContainerClick(event: MouseEvent): void {
    this.elementRef.nativeElement.focus();
  }

  public setDescribedByIds(ids: string[]): void {
    this.elementRef.nativeElement.setAttribute('aria-describedby', ids.join(' '));
  }
}
